import { IGraphData, IGraphDimensions } from 'Components/sq-graphics/interfaces';
import colors from '../../../../../config/theme/colors';
import { useIntl } from 'react-intl';
import { isNumber, partialDateFromDate } from 'neuro-utils';
import smileyFace from '../../../../../../public/assets/images/smiley-up.svg';
import ToolTip from 'Components/ToolTip';
import { statusSectionKeys } from 'Components/DashboardGraph/Settings/DataConversion/myapp';
import { formatPartialDate } from 'Utility/date';

const StatusSummary = ({ data, timeframeWidth, dimensions, areaHeight }: IOwnProps) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const latestDataPointsPerSection =
    data.scale.id === 'status'
      ? data.data
          .map(dat => {
            if (!('dataPoints' in dat)) return;
            const latestDataPoint = dat.dataPoints.reduce((prev, curr) => {
              return prev.date.getTime() > curr.date.getTime() ? prev : curr;
            });
            return latestDataPoint;
          })
          .filter(sec => sec)
      : undefined;

  const latestStatusDateStamp = latestDataPointsPerSection
    ? Math.max(...latestDataPointsPerSection.map(sec => sec?.date.getTime() ?? 0))
    : undefined;

  const latestDataSections = latestDataPointsPerSection?.filter(sec => sec?.date.getTime() === latestStatusDateStamp);

  const sortedSectionKeys: Array<(typeof statusSectionKeys)[0] | ''> = statusSectionKeys.toSorted((s1, s2) => {
    const value1 = latestDataSections?.find(sec => sec?.id === s1)?.value;
    const value2 = latestDataSections?.find(sec => sec?.id === s2)?.value;
    if (isNumber(value2) && isNumber(value1)) {
      return value2 - value1;
    }
    return 0;
  });

  // Filler
  sortedSectionKeys.push('');

  const circleSize = 24;

  return (
    <div
      style={{
        height: `${areaHeight}rem`,
        width: `${timeframeWidth}rem`,
        marginLeft: `${dimensions.graphs.scaleArea.width}rem`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2,
      }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div
        style={{
          backgroundColor: 'white',
          width: `${timeframeWidth}rem`,
          height: `${areaHeight}rem`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div>
          <p style={{ fontWeight: 600, color: colors.myms.primary, textAlign: 'center' }}>
            {fm('graph.selfReport.status.title') +
              (latestStatusDateStamp
                ? ' - ' + formatPartialDate(partialDateFromDate(new Date(latestStatusDateStamp)))
                : '')}
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            width: '100%',
            maxWidth: 800,
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'repeat(7, 34px)',
          }}
        >
          {sortedSectionKeys.map((section, i) => {
            const value = latestDataSections?.find(data => data?.id === section)?.value ?? '-';
            const isSmiley = isNumber(value) && value <= 5;
            return (
              <div
                key={section + `-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: Math.floor(i / 2) % 2 === 0 ? 'white' : colors.lightestGray,
                  borderLeft: i % 2 !== 0 ? `solid 2px ${colors.lightGray}` : undefined,
                  paddingLeft: 5,
                  paddingRight: 5,
                }}
              >
                {section !== '' && (
                  <>
                    <p>{fm(`graph.selfReport.status.opts.${section.split('VASValue')[0]}`)}</p>
                    <div
                      style={{
                        width: circleSize,
                        height: circleSize,
                        borderRadius: '50%',
                        border: isSmiley ? undefined : `1px solid ${colors.gray}`,
                        marginRight: isSmiley ? 1 : undefined,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {isSmiley ? (
                        <img src={smileyFace} width={circleSize} height={circleSize} />
                      ) : typeof value === 'string' ? (
                        <p>{value}</p>
                      ) : (
                        <ToolTip
                          title={`${value}`}
                          cursor="Pointer"
                          hover
                          content={
                            <div style={{ width: circleSize, height: circleSize }}>
                              <svg viewBox={`0 0 ${circleSize} ${circleSize}`} xmlns="http://www.w3.org/2000/svg">
                                <circle cx={circleSize / 2} cy={circleSize / 2} r={(value / 100) * 12} fill="#ff490b" />
                              </svg>
                            </div>
                          }
                        />
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface IOwnProps {
  data: IGraphData;
  timeframeWidth: number;
  dimensions: IGraphDimensions;
  areaHeight: number;
}

export default StatusSummary;
